import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Row, Tabs, TabsProps } from 'antd';
import { isNil } from 'lodash';
import moment from 'moment';

import BasePageCard from 'components/wrappers/BasePageCard';
import {
  AssignmentTypeEnum,
  OvertimeEnum,
  AssignmentStatusTypeEnum,
  BOUsersPriceAgreementRespModel,
  PriceAgreementW2InfoResponseModel,
  CustomerMspOrVmsOverviewDto,
  AssignmentMultiRateViewModel,
  PayRateTypeModel,
  AssignmentFinancialSchedulerViewModel,
  MultiRateSchedulerViewModel,
} from 'api/backofficeApiClient';
import {
  getPayTypeLabel,
  putSignOfNumberFrontOfSymbol,
  getOvertimeLabel,
  AssignmentDetailsModel,
} from 'utils/assignmentUtils';
import MultipleRatesDetails from './MultipleRatesDetails';

import {
  removeFinancialSchedulerInfo,
  updateFinancialSchedulerInfo,
} from 'redux/reducers/assignmentSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  isAdminSelector,
  isTeammateSelector,
  userSelector,
} from 'redux/selectors/authenticationSelectors';
import { AssignmentFinancialSchedulerEditModel } from 'api/adminApiClient';
import FinancialInfoScheduleModal, {
  EffectiveDateMode,
  FinancialSchedulerFormValues,
  MultiRateFormScheduleValues,
  PayCodeInfoFormScheduleValues,
} from 'components/modals/FinancialInfoScheduleModal';
import { getMemberPriceAgreement } from 'redux/reducers/recruiterSlice';
import { getPricingAgreement } from 'redux/reducers/accountSlice';
import { formatNumberAsCurrency, getFormattedNumber } from 'utils/numberUtils';
import {
  payCodesDetailsInfoSelector,
  payRateTypesSelector,
} from 'redux/selectors/assignmentSelectors';
import { PayCodeInfoFormValues } from 'components/modals/AssignmentDataModal/FinancialInfo';
import {
  mapFinancialInfoSchedulerToRequest,
  updateMultiRateSchedulersWithBilledIndicators,
} from 'components/modals/AssignmentDataModal/mapUtils';
import {
  financialInfoTabId,
  ptoMultiRateId,
} from 'constants/MultiRateConstants';
import { getPTOMultiRateDetails } from 'utils/multiRates/multiRatesUtils';

import styles from '../../AssignmentDetailsPage.module.less';

type AssignmentMultiRateModel =
  | AssignmentMultiRateViewModel
  | MultiRateSchedulerViewModel;

interface PayCodesInfoDisplayModel {
  payTypeId?: string;
  payRateTypeName?: string;
  payCodesInfo: any[];
}
type Props = {
  onSuccess: () => Promise<void>;
  assignmentDetails: AssignmentDetailsModel;
  financialInfoSchedulerInitialValues?: AssignmentFinancialSchedulerViewModel;
};

export default function FinancialInfoDetails(props: Props) {
  const { onSuccess, assignmentDetails, financialInfoSchedulerInitialValues } =
    props;
  const [
    isFinancialInfoSchedulerModalVisible,
    setIsFinancialInfoSchedulerModalVisible,
  ] = useState(false);
  const [priceAgreement, setPriceAgreement] =
    useState<BOUsersPriceAgreementRespModel>();
  const [activeKey, setActiveKey] = useState<string>(financialInfoTabId);

  const isAdminRole = useAppSelector(isAdminSelector);
  const isTeammate = useAppSelector(isTeammateSelector);
  const payRateTypes = useAppSelector(payRateTypesSelector);
  const payCodes = useAppSelector(payCodesDetailsInfoSelector);

  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const isW2Assignment =
    assignmentDetails.assignmentType === AssignmentTypeEnum.EEAssignment;

  useEffect(() => {
    loadPriceAgreement();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setActiveKey(financialInfoTabId);
  }, [assignmentDetails]);

  const w2InfoByWorkSiteWorkLocation = useMemo(() => {
    const w2InfoList = priceAgreement?.w2InfoList;
    const workStateLocation =
      assignmentDetails.assignmentDocument?.workLocation?.stateId;

    if (!w2InfoList?.length || !workStateLocation || !isW2Assignment) {
      return;
    }

    const w2InfoStateIds = w2InfoList.map((w2Info) => ({
      ...w2Info,
      placementStates: (w2Info.placementStates || []).map((state) => state.id),
    }));

    return w2InfoStateIds.find((w2Info) =>
      w2Info.placementStates.includes(workStateLocation),
    );
  }, [priceAgreement, assignmentDetails.assignmentDocument, isW2Assignment]);

  const { assignmentStatus, assignmentDocument, assignmentId } =
    assignmentDetails;

  if (!assignmentDocument) return null;

  const { multiRates = [], financialInfo, assignmentInfo } = assignmentDocument;

  const handleOpenDatesEffectiveModal = () => {
    setIsFinancialInfoSchedulerModalVisible(true);
  };

  const handleCloseDatesEffectiveModal = () => {
    setIsFinancialInfoSchedulerModalVisible(false);
  };

  const handleActiveTabChange = (activeKey: string) => {
    setActiveKey(activeKey);
  };

  const loadPriceAgreement = async () => {
    const correctAction: any = isAdminRole
      ? getMemberPriceAgreement
      : getPricingAgreement;

    const result = await dispatch(
      correctAction(isAdminRole ? user?.companyId : undefined),
    );

    if (correctAction.fulfilled.match(result)) {
      setPriceAgreement(result.payload);
    }
  };

  const handleSubmit = async (data: FinancialSchedulerFormValues) => {
    if (!assignmentId) return;

    const details: AssignmentFinancialSchedulerEditModel =
      mapFinancialInfoSchedulerToRequest(data);

    const result = await dispatch(
      updateFinancialSchedulerInfo({ assignmentId, details, isAdminRole }),
    );

    if (updateFinancialSchedulerInfo.fulfilled.match(result)) {
      BaseAlert.success('Changes have been scheduled.');
      onSuccess();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onRemoveFinancialScheduler = async () => {
    if (!assignmentId) return;

    const result = await dispatch(
      removeFinancialSchedulerInfo({ assignmentId, isAdminRole }),
    );

    if (removeFinancialSchedulerInfo.fulfilled.match(result)) {
      await onSuccess();
      handleCloseDatesEffectiveModal();
      BaseAlert.success('Scheduled changes have been removed.');
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const checkIsVisibleEditBtn = () => {
    return (
      assignmentStatus === AssignmentStatusTypeEnum.Active ||
      AssignmentStatusTypeEnum.Request ||
      (isAdminRole && assignmentStatus === AssignmentStatusTypeEnum.Completed)
    );
  };

  const isEffectiveDateBtn =
    financialInfoSchedulerInitialValues?.effectiveDate &&
    checkIsVisibleEditBtn();

  const renderChangesEffectiveDateBtn = () => {
    return (
      isEffectiveDateBtn && (
        <Col xs={24}>
          <div className={styles.changeBtnWrap}>
            <Button
              onClick={handleOpenDatesEffectiveModal}
              className={styles.changeBtn}
            >
              <i className="icon icon-edit" />
              Changes effective as of{' '}
              {moment(
                financialInfoSchedulerInitialValues?.effectiveDate,
              ).format('MM/DD/YYYY')}
            </Button>
          </div>
        </Col>
      )
    );
  };

  const getW2InfoByWorkSiteWorkLocation = (
    w2InfoList?: PriceAgreementW2InfoResponseModel[],
  ) => {
    const workSiteWorkLocation = assignmentDocument?.workLocation;

    if (!w2InfoList?.length || !workSiteWorkLocation?.stateId) {
      return;
    }
    const w2InfoStateIds = w2InfoList.map((w2Info) => ({
      ...w2Info,
      placementStates: (w2Info.placementStates || []).map((state) => state.id),
    }));
    const location = workSiteWorkLocation?.stateId;

    return w2InfoStateIds.find((w2Info) =>
      w2Info.placementStates.includes(location),
    );
  };

  const initialMBPRate = () => {
    const initialMBpRate =
      financialInfoSchedulerInitialValues?.financialInfoScheduler?.mbpRate;
    const w2InfoList = priceAgreement?.w2InfoList;

    if (initialMBpRate) {
      return initialMBpRate;
    }

    if (!isW2Assignment) {
      return priceAgreement?.icPrice || 0;
    }

    const w2InfoByWorkSiteWorkLocation =
      getW2InfoByWorkSiteWorkLocation(w2InfoList);

    const mbpRateCalculated = w2InfoByWorkSiteWorkLocation
      ? (w2InfoByWorkSiteWorkLocation.w2Price as number)
      : 0;
    return mbpRateCalculated;
  };

  const ptoMultiRateDetails = getPTOMultiRateDetails(payCodes, multiRates);

  const mapFinancialInfoSchedulerInitialValues = (
    scheduleData?: AssignmentFinancialSchedulerViewModel,
  ): FinancialSchedulerFormValues => {
    const { effectiveDate, multiRateSchedulers, financialInfoScheduler } =
      scheduleData || {};

    const updatedRateSchedules = updateMultiRateSchedulersWithBilledIndicators(
      assignmentDetails.assignmentDocument?.multiRates,
      multiRateSchedulers,
    );

    const initialValues: FinancialSchedulerFormValues = {
      effectiveDate,
      multiRates: [],
      financialInfoScheduler,
    };

    if (financialInfoScheduler) {
      const { payRate, billRate } = financialInfoScheduler;
      const hasMbpRate = !isNil(payRate) || !isNil(billRate);

      if (hasMbpRate) {
        const mbpRate = initialMBPRate();
        initialValues.financialInfoScheduler = {
          ...financialInfoScheduler,
          mbpRate,
        };
      }
    }

    const updatedRateSchedulesLength = updatedRateSchedules?.length;

    if (updatedRateSchedulesLength) {
      initialValues.multiRates = payRateTypes
        .map((payRateTypeDetails) =>
          getPayCodesInfo(
            payRateTypeDetails,
            updatedRateSchedules,
            ptoMultiRateDetails,
          ),
        )
        .map((value) => {
          const payCodesInfo =
            value?.payCodesInfo || ([] as PayCodeInfoFormValues[]);

          const item: MultiRateFormScheduleValues = {
            payTypeId: value?.payTypeId,
            activePayCodeId: payCodesInfo?.[0]?.payCodeId,
            payCodesInfo:
              payCodesInfo.length > 0
                ? payCodesInfo.map((payCode: PayCodeInfoFormValues) => {
                    let modifiedPayCodeInfo: PayCodeInfoFormScheduleValues = {
                      payCodeId: payCode?.payCodeId,
                      memberBilledIndicator: payCode?.memberBilledIndicator,
                      hasPayRateCustomLogic: payCode?.hasPayRateCustomLogic,
                      hasBillRateCustomLogic: payCode?.hasBillRateCustomLogic,
                    };

                    if (!isNil(payCode?.payRate)) {
                      modifiedPayCodeInfo.payRate = payCode?.payRate;
                    }

                    if (!isNil(payCode?.billRate)) {
                      modifiedPayCodeInfo.billRate = payCode?.billRate;
                    }

                    return modifiedPayCodeInfo;
                  })
                : undefined,
          };
          return item;
        })
        .filter((item) => {
          return Object.values(item).some((value) => value !== undefined);
        }) as MultiRateFormScheduleValues[];
    }

    return initialValues;
  };

  const mspOrVmsOverviewInfo: CustomerMspOrVmsOverviewDto = {
    mspFee: assignmentDetails.customerInfo?.mspFee as any,
    vmsFee: assignmentDetails.customerInfo?.vmsFee,
  };

  const renderFinancialInfoDetails = () => (
    <Row gutter={[8, 8]} className="titled-sheet">
      {isW2Assignment && !isNil(financialInfo.payType) && (
        <Col xs={24} md={12} data-id="assignmentPayType">
          <div className="title">Pay type:</div>
          <div>{getPayTypeLabel(financialInfo?.payType)}</div>
        </Col>
      )}

      {!isNil(financialInfo.payRate) && (
        <Col xs={24} md={12} data-id="assignmentPayRate">
          <div className="title">Base pay rate:</div>
          <div>
            {formatNumberAsCurrency({
              num: financialInfo.payRate,
            })}
          </div>
        </Col>
      )}

      {!isNil(financialInfo.billRate) && (
        <Col xs={24} md={12} data-id="assignmentBillRate">
          <div className="title">Bill rate:</div>
          <div>
            {formatNumberAsCurrency({
              num: financialInfo.billRate,
            })}
          </div>
        </Col>
      )}
      {!isTeammate && !isNil(financialInfo.profitRate) && (
        <Col xs={24} md={12} data-id="assignmentProfitRate">
          <div className="title">Profit rate:</div>
          <div>
            {putSignOfNumberFrontOfSymbol(financialInfo.profitRate)}
            {getFormattedNumber(Math.abs(financialInfo.profitRate), {
              separator: ',',
              toFixed: 2,
            })}
          </div>
        </Col>
      )}

      {!isNil(financialInfo.overtime) && (
        <Col xs={24} md={12} data-id="assignmentOvertime">
          <div className="title">Overtime:</div>
          <div>{getOvertimeLabel(financialInfo?.overtime)}</div>
        </Col>
      )}

      {financialInfo.overtime !== OvertimeEnum.Exempt &&
        !isNil(financialInfo.overtimePayRate) && (
          <Col xs={24} md={12} data-id="assignmentOvertimePayRate">
            <div className="title">OT pay rate:</div>
            <div>
              {formatNumberAsCurrency({
                num: financialInfo.overtimePayRate,
              })}
            </div>
          </Col>
        )}

      {!isNil(financialInfo.overtimeBillRate) && (
        <Col xs={24} md={12} data-id="assignmentOvertimeBillRate">
          <div className="title">OT bill rate:</div>
          <div>
            {formatNumberAsCurrency({
              num: financialInfo.overtimeBillRate,
            })}
          </div>
        </Col>
      )}

      {!isNil(financialInfo.dtBillRate) && (
        <Col xs={24} md={12} data-id="assignmentDTBillRate">
          <div className="title">DT bill rate:</div>
          <div>
            {formatNumberAsCurrency({
              num: financialInfo.dtBillRate,
            })}
          </div>
        </Col>
      )}

      {!isNil(financialInfo.dtPayRate) && (
        <Col xs={24} md={12} data-id="assignmentDTPayRate">
          <div className="title">DT pay rate:</div>
          <div>
            {formatNumberAsCurrency({
              num: financialInfo.dtPayRate,
            })}
          </div>
        </Col>
      )}

      {renderChangesEffectiveDateBtn()}
    </Row>
  );

  const getPayCodesInfo = (
    payRateType: PayRateTypeModel,
    multiRates: AssignmentMultiRateModel[] | MultiRateSchedulerViewModel[],
    ptoDetails?: AssignmentMultiRateViewModel,
  ): PayCodesInfoDisplayModel | null => {
    const payCodesForRateType = payCodes
      .filter((payCode) => payCode.payRateTypeId === payRateType.id)
      .map((payCode) => payCode.id);

    const multiRatesForPayCodes = (multiRates || [])
      .map((multiRate: AssignmentMultiRateModel) => {
        let extendedMultiRate: any = multiRate;

        const isPayCodeInRateType = payCodesForRateType.includes(
          extendedMultiRate.payCodeId,
        );

        if (!isPayCodeInRateType) return null;

        const payCodeInfo = payCodes.find(
          (payCode) => payCode.id === extendedMultiRate.payCodeId,
        );

        if (payRateType?.id === ptoMultiRateId && !isNil(ptoDetails)) {
          extendedMultiRate = {
            ...extendedMultiRate,
            memberBilledIndicator: ptoDetails?.memberBilledIndicator,
          };
        }

        return {
          ...extendedMultiRate,
          payCodeName: payCodeInfo?.name,
        };
      })
      .filter((info) => info !== null);

    if (multiRatesForPayCodes.length === 0) return null;

    return {
      payTypeId: payRateType.id || undefined,
      payRateTypeName: payRateType?.name || undefined,
      payCodesInfo: multiRatesForPayCodes || [],
    };
  };

  const getTabs = (): TabsProps['items'] => {
    const financialInfoTab = {
      label: 'Financial Info',
      key: financialInfoTabId,
      children: renderFinancialInfoDetails(),
      closable: false,
    };

    const getMultiRateTab = (multiRateInfo: PayCodesInfoDisplayModel) => {
      return {
        label: multiRateInfo?.payRateTypeName,
        key: multiRateInfo?.payTypeId as string,
        children: (
          <MultipleRatesDetails
            multiRateInfo={multiRateInfo}
            assignmentDetails={assignmentDetails}
          />
        ),
        closable: false,
      };
    };

    const multiRateTabs: TabsProps['items'] = payRateTypes
      .map((payRateTypeDetails) =>
        getPayCodesInfo(payRateTypeDetails, multiRates),
      )
      .filter((info) => !isNil(info))
      .map((multiRateInfo) =>
        getMultiRateTab(multiRateInfo as PayCodesInfoDisplayModel),
      );

    return [financialInfoTab, ...multiRateTabs];
  };

  return (
    <>
      {isFinancialInfoSchedulerModalVisible && (
        <FinancialInfoScheduleModal
          mode={EffectiveDateMode.EDIT}
          initialValues={mapFinancialInfoSchedulerInitialValues(
            financialInfoSchedulerInitialValues,
          )}
          onClose={handleCloseDatesEffectiveModal}
          onSuccess={handleSubmit}
          onRemoveFinancialScheduler={onRemoveFinancialScheduler}
          workStateLocation={assignmentDocument?.workLocation.stateId}
          isW2Assignment={isW2Assignment}
          w2InfoByWorkSiteWorkLocation={w2InfoByWorkSiteWorkLocation}
          mspOrVmsOverviewInfo={mspOrVmsOverviewInfo}
          assignmentType={assignmentDetails.assignmentType || null}
          ptoMultiRate={ptoMultiRateDetails}
          jobCategoryValue={assignmentInfo?.jobCategory}
        />
      )}

      <Col xs={24} md={12} className="d-flex">
        <BasePageCard
          className="stretch-card"
          title="Financial information"
          icons={<span className="icon icon-pay" />}
        >
          <div className="content-wrapper">
            <div className={styles.tabs}>
              <Tabs
                type="editable-card"
                items={getTabs()}
                activeKey={activeKey}
                onChange={handleActiveTabChange}
                hideAdd={true}
              />
            </div>
          </div>
        </BasePageCard>
      </Col>
    </>
  );
}
